import { Column, Flex } from "components/LEGACY/styled-components/Flex"
import { EditOutline, ImageAdd as ImgAdd, TrashIcon } from "components/LEGACY/styled-components/Icons"
import IconButton from "components/utils/IconButton"
import { CollectionReference, DocumentReference } from "firebase/firestore"
import { getDownloadURL, uploadBytesResumable } from "firebase/storage"
import { useDisplayToast } from "hooks/misc/useDisplayToast"
import React, { useRef, useState } from "react"
import styled from "styled-components"
import { BlurImage } from "types/firestore/productGroup"
import { generateImagePlaceholder } from "util-functions/generateImagePlaceholder"
import { getImageUploadRef, getImageUploadRefForCollection } from "util-functions/getImageUploadRef"

const ImageOverlay = styled.div`
  width: 100%;
  height: 100%;
  background-color: black !important;
  opacity: 0%;
  visibility: hidden;
  position: absolute;
  top: 0;
`
const ImageAdd = styled(ImgAdd)<{ $empty?: boolean }>`
  visibility: ${props => (props.$empty ? "visible" : "hidden")};
  position: absolute;
  top: calc(50% - 2rem);
  left: calc(50% - 2rem);
`

interface ImageContainerProps {
  $compact?: boolean
}

const ImageContainer = styled.div<ImageContainerProps>`
  align-items: center;
  border: 1px solid ${props => props.theme.greyBorder};

  border-radius: 15px;
  cursor: pointer;
  display: flex;
  width: ${props => (props.$compact ? "128px" : "200px")};
  height: ${props => (props.$compact ? "128px" : "200px")};
  max-width: 100%;

  position: relative;

  &:hover ${ImageOverlay} {
    opacity: 70%;
    visibility: visible;
    transition:
      visibility 0s,
      opacity 0.5s linear;
    border-radius: 15px;
  }
  &:hover ${ImageAdd} {
    visibility: visible;
    transition: visibility 0s linear;
  }
`

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 15px;
`

export interface ImageUploaderProps {
  image?: BlurImage | null
  setImage: (image?: BlurImage) => void
  /** The path of the document will be the storage location */
  relatedDocument?: DocumentReference
  /** Collection path (used if relatedDocument is not provided) */
  relatedCollection?: CollectionReference
  /** Quality settings for the image */
  qualitySettings?: {
    /** If true, the image won't be compressed */
    skipCompression?: boolean
    /** Quality level (1-100) for compression, higher means better quality */
    qualityLevel?: number
  }
  /** Use a smaller square in multi-image editors. */
  compact?: boolean
}

const ImageUploader = ({
  image,
  setImage,
  relatedDocument,
  relatedCollection,
  qualitySettings,
  compact = false,
}: ImageUploaderProps) => {
  const [loading, setLoading] = useState(false)
  const displayToast = useDisplayToast()

  const readFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetRef = relatedDocument
      ? getImageUploadRef(relatedDocument)
      : relatedCollection
        ? getImageUploadRefForCollection(relatedCollection)
        : undefined

    if (targetRef && e.target.files && e.target.files[0]) {
      try {
        setLoading(true)
        // Create metadata with quality settings
        const metadata = {
          customMetadata: {
            ...(qualitySettings?.skipCompression && { skipCompression: "true" }),
            ...(qualitySettings?.qualityLevel && { qualityLevel: qualitySettings.qualityLevel.toString() }),
          },
        }

        // Use uploadBytesResumable to include metadata
        const upload = await uploadBytesResumable(targetRef, e.target.files[0], metadata)
        const url = await getDownloadURL(upload.ref)
        const placeholder = await generateImagePlaceholder(URL.createObjectURL(e.target.files[0]))

        setImage({
          url: url,
          ...placeholder,
        })

        setLoading(false)
      } catch (error) {
        console.error(error)
        displayToast("Fehler: Das Bild konnte nicht hochgeladen werden.", "error")
        setLoading(false)
      }
    }
  }

  const imageInputRef = useRef<HTMLInputElement>(null)

  return (
    <>
      <style jsx>{`
        .actionButtonWrapper {
          display: flex;
          position: absolute;
          top: 0.25rem;
          right: 0.25rem;
        }
      `}</style>
      <Flex>
        <Column width="100%" padding="0">
          <ImageContainer
            $compact={compact}
            onClick={() => {
              imageInputRef.current?.click()
            }}
            onKeyDown={event => {
              if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault()
                imageInputRef.current?.click()
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={image?.url ? "Bild ersetzen" : "Bild hinzufügen"}
          >
            <ImageOverlay />
            <ImageAdd $empty={!image?.url} size={compact ? "40" : "64"} />
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={readFile}
              hidden
              disabled={loading || (!relatedDocument && !relatedCollection)}
            />
            {image?.url && image?.url !== "none" && (
              <>
                <Image src={image?.url} alt="Bildvorschau" />
                <div className="actionButtonWrapper">
                  <IconButton
                    style={{ background: "white", marginRight: "0.25rem" }}
                    // no onClick needed, as the event propagates up into the container
                    icon={<EditOutline size="22" color="currentColor" />}
                  />
                  <IconButton
                    style={{ background: "white" }}
                    onClick={e => {
                      e.stopPropagation()
                      e.preventDefault()
                      setImage(undefined)
                    }}
                    icon={<TrashIcon size="22" color="currentColor" />}
                  />
                </div>
              </>
            )}
          </ImageContainer>
        </Column>
      </Flex>
    </>
  )
}

export default ImageUploader
